import type React from 'react'
import {Navigate} from 'react-router-dom'

import {LoadingPage} from '@pleo-io/telescope'

import {useTrustedBeneficiaryConsent} from '@product-web/feature--admin-consent/use-trusted-beneficiaries'
import {useCompanyOnboardingStatus} from '@product-web/feature--onboarding/use-company-onboarding-status'
import {useSCAOnboarding} from '@product-web/feature--sca/sca'
import {useMissingKycInformation} from '@product-web/shared--api-deimos/kyc/use-kyc-information'
import {useFlags, useFlagsLoaded} from '@product-web/shared--flags'
import {EmployeeStatus} from '@product-web/shared--shell/employee-status'
import {useIsCompanyOnboardedViaB4B} from '@product-web/shared--user'
import {getIsFdd, useLoggedInUser} from '@product-web/shared--user'

/**
 * Component handling the index route (/) redirects for the page. Since we don't serve any content for the
 * index route, we always want to redirect the user somewhere. The redirect location depends on user's
 * role, user's company state
 */
const IndexRoute: React.FC = () => {
    // This redirect to login page is handled in the auth-route wrapper
    const user = useLoggedInUser()
    const featureFlags = useFlags()
    const featureFlagsLoaded = useFlagsLoaded()
    const isOnboardedViaB4B = useIsCompanyOnboardedViaB4B()

    // We trigger the onboarding flow to enroll users to 2FA.
    // This allows the user to verify their phone number and then to re-login
    const {isLoading: isLoadingSCAOnboardingCheck} = useSCAOnboarding()

    if (isLoadingSCAOnboardingCheck) {
        // We show loader while we're checking if user is SCA enabled or not
        return <LoadingPage />
    }

    // We navigate to the company-verification page if the company is onboarded via B4B
    if (featureFlagsLoaded && featureFlags.b4BNav && isOnboardedViaB4B) {
        return <Navigate to="/company-verification" replace />
    }

    // If the user is part of an accounting partnership they should always land in the partner
    // portal. Partner users can have several roles (partner-*, bookkeeper-*, owner/member), so
    // we're checking if user has a partner emplyee
    if (user.partnerEmployee) {
        return <Navigate to="/partner" replace />
    }

    // the user is a company admin, handle separately
    if (user.role === 'owner') {
        return <OwnerUserIndexRoute />
    }

    // the user is a company bookkeeper-extended, handle separately
    if (user.role === 'bookkeeper-extended') {
        return <BookkeeperExtendedUserIndexRoute />
    }

    // if the user is not admin/partner/bookkeeper, the company is not yet FDDed,
    // and the user has not dismissed the get-started screen, show it
    if (
        user.employee?.status?.state?.[EmployeeStatus.getStarted] !== 'hide' &&
        !user.company?.status.fdd
    ) {
        return <Navigate to="/getstarted" replace />
    }

    // the default route view is expenses
    return <Navigate to="/expenses" replace />
}

const OwnerUserIndexRoute = () => {
    useTrustedBeneficiaryConsent()
    const user = useLoggedInUser()
    const featureFlags = useFlags()
    const featureFlagsLoaded = useFlagsLoaded()
    const isOnboardedViaB4B = useIsCompanyOnboardedViaB4B()
    const {data: missingInfo, error: missingInfoError} = useMissingKycInformation()
    const awaitingMissingInfo = missingInfo === undefined && missingInfoError === undefined
    const hasMissingCompanyInfo = Boolean(user.companyId) && Number(missingInfo?.length) > 0
    const {awaitingCompanyOnboardingStatus, shouldHideCompanyOnboarding} =
        useCompanyOnboardingStatus()

    if (awaitingMissingInfo || awaitingCompanyOnboardingStatus) {
        return <LoadingPage />
    }

    // We navigate to the company-verification page if the company is onboarded via B4B
    if (featureFlagsLoaded && featureFlags.b4BNav && isOnboardedViaB4B) {
        return <Navigate to="/company-verification" replace />
    }

    // Redirect the user to the onboarding screen if it is not hidden
    if (!shouldHideCompanyOnboarding) {
        return <Navigate to="/onboarding" replace />
    }

    if (!getIsFdd(user) && hasMissingCompanyInfo) {
        return <Navigate to="/company-verification" replace />
    }

    // the default route view is expenses
    return <Navigate to="/expenses" replace />
}

const BookkeeperExtendedUserIndexRoute = () => {
    const user = useLoggedInUser()
    const featureFlags = useFlags()
    const featureFlagsLoaded = useFlagsLoaded()
    const isOnboardedViaB4B = useIsCompanyOnboardedViaB4B()
    const {data: missingInfo, error: missingInfoError} = useMissingKycInformation()
    const awaitingMissingInfo = missingInfo === undefined && missingInfoError === undefined

    if (awaitingMissingInfo) {
        return <LoadingPage />
    }

    // We navigate to the company-verification page if the company is onboarded via B4B
    if (featureFlagsLoaded && featureFlags.b4BNav && isOnboardedViaB4B) {
        return <Navigate to="/company-verification" replace />
    }

    // If the company is not yet FDDed and they are missing some KYC
    // information, take the bookkeeper to company KYC page
    if (!getIsFdd(user) && (missingInfo?.length ?? 0) > 0) {
        return <Navigate to="/company-verification" replace />
    }

    // the default route view is expenses
    return <Navigate to="/expenses" replace />
}

export const Component = IndexRoute
